import type { Locale } from '@shared/config/i18n/locales'
import {
  useMutation,
  useQueryClient,
  queryOptions,
  infiniteQueryOptions,
  keepPreviousData,
  InfiniteData,
} from '@tanstack/react-query'

import { TutorConnectBookRequest, TutorReview } from '../model/types'
import {
  BrowseTutorsPageResponse,
  bookWithDesignatedTutor,
  deleteReview,
  favoriteTutor,
  getAvailableDesignatedTutors,
  getBrowseTutors,
  getFavoriteAvailableSlots,
  getFavoriteTutors,
  getTutorConnectEntrySummary,
  getTutorConnectFavorites,
  getTutorConnectGrants,
  getTutorConnectLedger,
  getTutorConnectSummary,
  getTutorOpenSchedule,
  getReservationLimits,
  getTutorMemo,
  getTutorReviews,
  getTutorReviewSummary,
  getTutorTeachable,
  getTutorWeeklyAvailability,
  likeReview,
  unfavoriteTutor,
  unlikeReview,
  updateTutorMemo,
} from './tutor-connect.api'

export const tutorConnectEntityQueries = {
  all: ['tutorConnect'],
  summaryKey: (bearerToken?: string) => [...tutorConnectEntityQueries.all, 'summary', bearerToken],
  entrySummaryKey: (bearerToken?: string) => [...tutorConnectEntityQueries.all, 'entrySummary', bearerToken],
  grantsKey: (bearerToken?: string) => [...tutorConnectEntityQueries.all, 'grants', bearerToken],
  ledgerKey: (bearerToken?: string) => [...tutorConnectEntityQueries.all, 'ledger', bearerToken],
  favoritesKey: (bearerToken?: string) => [...tutorConnectEntityQueries.all, 'favorites', bearerToken],
  availableTutorsKey: (classId: number | null, utcStart: string | null, bearerToken?: string, locale?: Locale) => [
    ...tutorConnectEntityQueries.all,
    'availableTutors',
    classId,
    utcStart,
    bearerToken,
    locale,
  ],
  browseTutorsKey: (langType: string, keyword: string, bearerToken?: string, locale?: Locale) => [
    ...tutorConnectEntityQueries.all,
    'browseTutors',
    langType,
    keyword,
    bearerToken,
    locale,
  ],
  favoriteTutorsKey: (bearerToken?: string, locale?: Locale) => [
    ...tutorConnectEntityQueries.all,
    'favoriteTutors',
    bearerToken,
    locale,
  ],
  tutorMemoKey: (tutorId: number, bearerToken?: string) => [
    ...tutorConnectEntityQueries.all,
    'tutorMemo',
    tutorId,
    bearerToken,
  ],
  tutorReviewSummaryKey: (tutorId: number, bearerToken?: string) => [
    ...tutorConnectEntityQueries.all,
    'tutorReviewSummary',
    tutorId,
    bearerToken,
  ],
  tutorReviewsKey: (tutorId: number | null, sort?: 'recommend' | 'latest', bearerToken?: string) => [
    ...tutorConnectEntityQueries.all,
    'tutorReviews',
    tutorId,
    sort ?? null,
    bearerToken,
  ],
  tutorWeeklyAvailabilityKey: (
    tutorId: number | null,
    classId: number | null,
    utcStart: string | null,
    bearerToken?: string,
  ) => [...tutorConnectEntityQueries.all, 'tutorWeeklyAvailability', tutorId, classId, utcStart, bearerToken],
  tutorOpenScheduleKey: (tutorId: number | null, subscribeMappId: string | null, bearerToken?: string) => [
    ...tutorConnectEntityQueries.all,
    'tutorOpenScheduleBrowse',
    tutorId,
    subscribeMappId,
    bearerToken,
  ],
  tutorOpenSchedulePreviewKey: (tutorId: number | null, bearerToken?: string) => [
    ...tutorConnectEntityQueries.all,
    'tutorOpenSchedulePreview',
    tutorId,
    bearerToken,
  ],
  tutorTeachableKey: (tutorId: number | null, bearerToken?: string) => [
    ...tutorConnectEntityQueries.all,
    'tutorTeachable',
    tutorId,
    bearerToken,
  ],
  favoriteAvailableSlotsKey: (
    classId: number | null,
    utcStart: string | null,
    utcEnd: string | null,
    bearerToken?: string,
  ) => [...tutorConnectEntityQueries.all, 'favoriteAvailableSlots', classId, utcStart, utcEnd, bearerToken],

  summary: (bearerToken: string) =>
    queryOptions({
      queryKey: tutorConnectEntityQueries.summaryKey(bearerToken),
      queryFn: async () => {
        const response = await getTutorConnectSummary(bearerToken)
        return response.data
      },
    }),

  entrySummary: (bearerToken: string) =>
    queryOptions({
      queryKey: tutorConnectEntityQueries.entrySummaryKey(bearerToken),
      queryFn: async () => {
        const response = await getTutorConnectEntrySummary(bearerToken)
        return response.data
      },
    }),

  grants: (bearerToken: string) =>
    queryOptions({
      queryKey: tutorConnectEntityQueries.grantsKey(bearerToken),
      queryFn: async () => {
        const response = await getTutorConnectGrants(bearerToken)
        return response.data
      },
    }),

  ledger: (bearerToken: string) =>
    queryOptions({
      queryKey: tutorConnectEntityQueries.ledgerKey(bearerToken),
      queryFn: async () => {
        const response = await getTutorConnectLedger(bearerToken)
        return response.data
      },
    }),

  favorites: (bearerToken: string) =>
    queryOptions({
      queryKey: tutorConnectEntityQueries.favoritesKey(bearerToken),
      queryFn: async () => {
        const response = await getTutorConnectFavorites(bearerToken)
        return response.data
      },
    }),

  availableTutors: (request: {
    bearerToken: string
    classId: number | null
    utcStart: string | null
    locale?: Locale
  }) =>
    queryOptions({
      queryKey: tutorConnectEntityQueries.availableTutorsKey(
        request.classId,
        request.utcStart,
        request.bearerToken,
        request.locale,
      ),
      queryFn: async () => {
        if (!request.classId || !request.utcStart) {
          return []
        }
        const response = await getAvailableDesignatedTutors({
          bearerToken: request.bearerToken,
          classId: request.classId,
          utcStart: request.utcStart,
          locale: request.locale,
        })
        return response.data
      },
      enabled: Boolean(request.classId && request.utcStart),
    }),

  // keyword: 이름 검색어(공백 trim 후). 빈 문자열이면 전체 목록. 검색어별로 캐시가 분리된다.
  browseTutors: (langType: string, bearerToken: string, keyword = '', locale?: Locale) =>
    infiniteQueryOptions({
      queryKey: tutorConnectEntityQueries.browseTutorsKey(langType, keyword, bearerToken, locale),
      queryFn: async ({ pageParam }) => {
        const response = await getBrowseTutors(langType, bearerToken, pageParam as number, 20, keyword, locale)
        return response.data
      },
      initialPageParam: 0,
      getNextPageParam: (lastPage) => (lastPage.hasNext ? lastPage.page + 1 : undefined),
    }),

  favoriteTutors: (bearerToken: string, locale?: Locale) =>
    queryOptions({
      queryKey: tutorConnectEntityQueries.favoriteTutorsKey(bearerToken, locale),
      queryFn: async () => {
        const response = await getFavoriteTutors(bearerToken, locale)
        return response.data
      },
    }),

  /**
   * 튜터 프로필 "내 메모". 찜 여부와 무관하게 단건 조회한다 —
   * 찜하지 않은 튜터의 메모는 찜 목록(favoriteTutors)에 실려 오지 않으므로 목록에 기댈 수 없다.
   */
  tutorMemo: (bearerToken: string, tutorId: number) =>
    queryOptions({
      queryKey: tutorConnectEntityQueries.tutorMemoKey(tutorId, bearerToken),
      queryFn: async () => {
        const response = await getTutorMemo(bearerToken, tutorId)
        return response.data
      },
      enabled: Boolean(tutorId),
    }),

  tutorReviewSummary: (bearerToken: string, tutorId: number) =>
    queryOptions({
      queryKey: tutorConnectEntityQueries.tutorReviewSummaryKey(tutorId, bearerToken),
      queryFn: async () => {
        const response = await getTutorReviewSummary(bearerToken, tutorId)
        return response.data
      },
    }),

  tutorReviews: (bearerToken: string, tutorId: number | null, sort: 'recommend' | 'latest' = 'recommend') =>
    queryOptions({
      queryKey: tutorConnectEntityQueries.tutorReviewsKey(tutorId, sort, bearerToken),
      queryFn: async () => {
        if (!tutorId) {
          return []
        }
        const response = await getTutorReviews(bearerToken, tutorId, sort)
        return response.data
      },
      // 정렬 토글 시 이전 데이터를 유지해 깜빡임을 막는다.
      placeholderData: keepPreviousData,
      enabled: Boolean(tutorId),
    }),

  tutorWeeklyAvailability: (
    bearerToken: string,
    tutorId: number | null,
    classId: number | null,
    utcStart: string | null,
  ) =>
    queryOptions({
      queryKey: tutorConnectEntityQueries.tutorWeeklyAvailabilityKey(tutorId, classId, utcStart, bearerToken),
      queryFn: async () => {
        if (!tutorId || !classId) {
          return []
        }
        const response = await getTutorWeeklyAvailability(bearerToken, tutorId, classId, utcStart)
        return response.data
      },
      enabled: Boolean(tutorId && classId),
    }),

  // 프로필-origin 신규 예약의 일정 조회. 매핑은 발행 수강권 기간을 합칠 때만 사용하며,
  // 서버에는 넘기지 않아 예약 한도 때문에 실제 튜터 일정이 숨겨지지 않도록 한다.
  tutorOpenSchedule: (bearerToken: string, tutorId: number | null, subscribeMappId: string | null) =>
    queryOptions({
      queryKey: tutorConnectEntityQueries.tutorOpenScheduleKey(tutorId, subscribeMappId, bearerToken),
      queryFn: async () => {
        if (!tutorId || !subscribeMappId) {
          return []
        }
        const response = await getTutorOpenSchedule(bearerToken, tutorId)
        return response.data
      },
      enabled: Boolean(tutorId && subscribeMappId),
    }),

  // 튜터 프로필의 단순 조회용. 수강권 문맥을 넘기지 않아 무제한권 1일 1회 정책을 적용하지 않는다.
  tutorOpenSchedulePreview: (bearerToken: string, tutorId: number | null) =>
    queryOptions({
      queryKey: tutorConnectEntityQueries.tutorOpenSchedulePreviewKey(tutorId, bearerToken),
      queryFn: async () => {
        if (!tutorId) {
          return []
        }
        const response = await getTutorOpenSchedule(bearerToken, tutorId)
        return response.data
      },
      enabled: Boolean(tutorId),
    }),

  reservationLimits: (bearerToken: string, subscribeMappId: string) =>
    queryOptions({
      queryKey: [...tutorConnectEntityQueries.all, 'reservationLimits', subscribeMappId, bearerToken],
      queryFn: async () => (await getReservationLimits(bearerToken, subscribeMappId)).data,
      enabled: Boolean(bearerToken && subscribeMappId),
      staleTime: 0,
    }),

  tutorTeachable: (bearerToken: string, tutorId: number | null) =>
    queryOptions({
      queryKey: tutorConnectEntityQueries.tutorTeachableKey(tutorId, bearerToken),
      queryFn: async () => {
        if (!tutorId) {
          return null
        }
        const response = await getTutorTeachable(bearerToken, tutorId)
        return response.data
      },
      enabled: Boolean(tutorId),
    }),

  favoriteAvailableSlots: (
    bearerToken: string,
    classId: number | null,
    utcStart: string | null,
    utcEnd: string | null,
  ) =>
    queryOptions({
      queryKey: tutorConnectEntityQueries.favoriteAvailableSlotsKey(classId, utcStart, utcEnd, bearerToken),
      queryFn: async () => {
        if (!classId || !utcStart || !utcEnd) {
          return []
        }
        const response = await getFavoriteAvailableSlots(bearerToken, classId, utcStart, utcEnd)
        return response.data.slotDateTimes ?? []
      },
      enabled: Boolean(classId),
    }),
}

/**
 * 찜/찜취소를 모든 관련 캐시에 즉시 반영한다 (낙관적 업데이트).
 * 찜 여부는 favorites 목록뿐 아니라 튜터 리스트(availableTutors/favoriteTutors/browseTutors)의
 * 각 항목 `favorite` 필드로도 노출되므로 함께 토글한다.
 */
const applyFavoriteToCaches = (queryClient: ReturnType<typeof useQueryClient>, tutorId: number, favorite: boolean) => {
  // favorites 목록: 찜 추가면 항목 추가/갱신, 찜 해제면 목록에서 제거(즉시 사라지도록).
  queryClient.setQueriesData<Array<{ tutorId: number; favorite: boolean }>>(
    { queryKey: [...tutorConnectEntityQueries.all, 'favorites'] },
    (old) => {
      if (!Array.isArray(old)) {
        return old
      }
      if (!favorite) {
        return old.filter((item) => item.tutorId !== tutorId)
      }
      return old.some((item) => item.tutorId === tutorId)
        ? old.map((item) => (item.tutorId === tutorId ? { ...item, favorite } : item))
        : [...old, { tutorId, favorite }]
    },
  )

  // 튜터 카드 목록(배열 캐시): 항목의 favorite 필드를 토글
  for (const prefix of ['availableTutors', 'favoriteTutors'] as const) {
    queryClient.setQueriesData<Array<{ tutorId: number; favorite?: boolean }>>(
      { queryKey: [...tutorConnectEntityQueries.all, prefix] },
      (old) =>
        Array.isArray(old) ? old.map((item) => (item.tutorId === tutorId ? { ...item, favorite } : item)) : old,
    )
  }

  // browseTutors 는 무한스크롤(InfiniteData) 캐시이므로 pages 안의 tutors 를 토글한다.
  queryClient.setQueriesData<InfiniteData<BrowseTutorsPageResponse>>(
    { queryKey: [...tutorConnectEntityQueries.all, 'browseTutors'] },
    (old) =>
      old && 'pages' in old
        ? {
            ...old,
            pages: old.pages.map((p) => ({
              ...p,
              tutors: p.tutors.map((t) => (t.tutorId === tutorId ? { ...t, favorite } : t)),
            })),
          }
        : old,
  )
}

/** 찜 토글 뮤테이션 공통 옵션 (낙관적 업데이트 + 실패 시 롤백 + 정산 후 무효화) */
const useToggleFavoriteMutation = (
  bearerToken: string,
  favorite: boolean,
  mutationFn: (tutorId: number) => Promise<unknown>,
) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn,
    onMutate: async (tutorId: number) => {
      await queryClient.cancelQueries({ queryKey: tutorConnectEntityQueries.all })
      const snapshot = queryClient.getQueriesData({ queryKey: tutorConnectEntityQueries.all })
      applyFavoriteToCaches(queryClient, tutorId, favorite)
      return { snapshot }
    },
    onError: (_error, _tutorId, context) => {
      context?.snapshot.forEach(([key, data]) => queryClient.setQueryData(key, data))
    },
    onSettled: () => {
      // A background refresh must not hold an already acknowledged preference pending.
      void queryClient.invalidateQueries({ queryKey: tutorConnectEntityQueries.all }).catch(() => undefined)
    },
  })
}

export const useFavoriteTutorMutation = (bearerToken: string) =>
  useToggleFavoriteMutation(bearerToken, true, (tutorId) => favoriteTutor(bearerToken, tutorId))

export const useUnfavoriteTutorMutation = (bearerToken: string) =>
  useToggleFavoriteMutation(bearerToken, false, (tutorId) => unfavoriteTutor(bearerToken, tutorId))

/**
 * 메모 저장. 메모는 찜과 독립이므로 찜 상태 관련 쿼리는 건드리지 않는다.
 * 무효화 대상은 카드에 메모를 함께 그리는 튜터 목록뿐이다 — 자동저장이 입력 중 1.5초마다
 * 호출되므로, 예전처럼 tutorConnect 전체를 무효화하면 스케줄/잔여횟수/리뷰까지 매번 재조회된다.
 */
const MEMO_AFFECTED_LIST_PREFIXES = ['availableTutors', 'browseTutors', 'favoriteTutors'] as const

export const useUpdateTutorMemoMutation = (bearerToken: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ tutorId, memo }: { tutorId: number; memo: string }) => updateTutorMemo(bearerToken, tutorId, memo),
    onSuccess: async (response, { tutorId }) => {
      queryClient.setQueryData(tutorConnectEntityQueries.tutorMemoKey(tutorId, bearerToken), response.data)
      await Promise.all(
        MEMO_AFFECTED_LIST_PREFIXES.map((prefix) =>
          queryClient.invalidateQueries({ queryKey: [...tutorConnectEntityQueries.all, prefix] }),
        ),
      )
    },
  })
}

/**
 * 리뷰 도움돼요(따봉) 토글 뮤테이션 (낙관적 업데이트 + 실패 시 롤백 + 성공 시 서버값 보정).
 * 리뷰 목록(tutorReviews) 캐시에서 해당 리뷰의 liked/likeCount 만 in-place 로 갱신한다.
 * 목록 재조회(invalidate)는 하지 않는다 — 따봉 토글로 순서가 바뀌거나 화면이 상단으로 튀지 않게 한다.
 */
export const useToggleReviewLikeMutation = (bearerToken: string) => {
  const queryClient = useQueryClient()
  const reviewsKey = [...tutorConnectEntityQueries.all, 'tutorReviews']
  return useMutation({
    mutationFn: ({ reviewId, liked }: { reviewId: number; liked: boolean }) =>
      liked ? likeReview(bearerToken, reviewId) : unlikeReview(bearerToken, reviewId),
    onMutate: async ({ reviewId, liked }) => {
      await queryClient.cancelQueries({ queryKey: reviewsKey })
      const snapshot = queryClient.getQueriesData<TutorReview[]>({ queryKey: reviewsKey }).flatMap(([key, reviews]) => {
        const review = reviews?.find((review) => review.id === reviewId)
        return review ? [{ key, liked: review.liked, likeCount: review.likeCount }] : []
      })
      queryClient.setQueriesData<TutorReview[]>({ queryKey: reviewsKey }, (old) =>
        Array.isArray(old)
          ? old.map((review) =>
              review.id === reviewId
                ? { ...review, liked, likeCount: Math.max(0, (review.likeCount ?? 0) + (liked ? 1 : -1)) }
                : review,
            )
          : old,
      )
      return { snapshot }
    },
    onError: (_error, { reviewId, liked: optimisticLiked }, context) => {
      // 삭제/재조회 이후의 목록은 유지하고, 아직 남아 있는 대상 리뷰의 좋아요 필드만 되돌린다.
      context?.snapshot.forEach(({ key, liked, likeCount }) =>
        queryClient.setQueryData<TutorReview[]>(key, (current) =>
          current?.map((review) =>
            review.id === reviewId &&
            review.liked === optimisticLiked &&
            review.likeCount === Math.max(0, (likeCount ?? 0) + (optimisticLiked ? 1 : -1))
              ? { ...review, liked, likeCount }
              : review,
          ),
        ),
      )
    },
    onSuccess: (response) => {
      // 서버가 돌려준 정확한 liked/likeCount 로 해당 리뷰만 보정한다(목록 재조회 없이 in-place).
      const result = response.data
      queryClient.setQueriesData<TutorReview[]>({ queryKey: reviewsKey }, (old) =>
        Array.isArray(old)
          ? old.map((review) =>
              review.id === result.reviewId ? { ...review, liked: result.liked, likeCount: result.likeCount } : review,
            )
          : old,
      )
    },
  })
}

export const useDeleteReviewMutation = (bearerToken: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (reviewId: number) => deleteReview(bearerToken, reviewId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: tutorConnectEntityQueries.all })
    },
  })
}

export const useDesignatedTutorBookingMutation = (bearerToken: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (request: TutorConnectBookRequest) => bookWithDesignatedTutor(bearerToken, request),
    // 응답 유실 시 이미 예약되었을 수 있어 자동 재전송하지 않는다.
    retry: false,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: tutorConnectEntityQueries.all })
    },
  })
}
